import { Component, ViewChild, ViewEncapsulation } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FirmSettings } from '@core/entity/firm.settings';
import { Role, User } from '@core/entity/user';
import { ActivityService } from '@core/services/activity.service';
import { FirmService } from '@core/services/firm.service';
import { PeriodService } from '@core/services/period.service';
import { UserService } from '@core/services/user.service';
import { FirmActions } from '@core/state/firm/firm.actions';
import { FirmSelectors } from '@core/state/firm/firm.selectors';
import { ToastActions } from '@core/state/toast/toast.actions';
import { FirmType } from '@core/state/types';
import { UserSettingsStorage } from '@core/storage/user.settings.storage';
import { TabsComponent } from '@shared/components/tabs/tabs.component';
import { DialogData } from '../shared/components/form-dialog-base/form-dialog-base.component';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslatableFormDialogBaseComponent } from '@core/i18n/translatable-form-dialog-base.component';
import { getTranslationProvider } from '../core/i18n/getTranslationProvider';
import { TranslatableStringType } from '../core/i18n/types';
// import { AnalyticActions } from '../core/state/analytics/analytics.actions';
// import { AnalyticsAction } from '../core/entity/analytics/analytics-actions';

// TODO - testa att firm settings funkar. lägg tillbaka stash'en och med analytics för toggle av menyn!

interface FirmSettingsDialogTranslation {
  firmSettings: TranslatableStringType;
  firmData: TranslatableStringType;
  firmUser: TranslatableStringType;
  taskTemplates: TranslatableStringType;
  assignmentSettings: TranslatableStringType;
  bulkHandling: TranslatableStringType;
  bulkHandlingTooltip: TranslatableStringType;
  teamManagement: TranslatableStringType;
  teamManagementTooltip: TranslatableStringType;
  systemSettings: TranslatableStringType;
  import: TranslatableStringType;
  firmAdminTools: TranslatableStringType;
  firmAssignmentSuccessMessage: TranslatableStringType;
  generalErrorTitle: TranslatableStringType;
  generalErrorMessage: TranslatableStringType;
  resetSuccessMessage: TranslatableStringType;
}

const TRANSLATION_SCOPE = 'firmSettingsDialog';

interface Data extends DialogData {
  tabIdToOpen?: string;
}

@Component({
  selector: 'app-firm-settings-dialog',
  templateUrl: './firm.settings.dialog.html',
  styleUrls: ['./firm.settings.dialog.css'],
  providers: [PeriodService, ActivityService, getTranslationProvider(TRANSLATION_SCOPE)],
  encapsulation: ViewEncapsulation.None,
})
export class FirmSettingsDialog extends TranslatableFormDialogBaseComponent<Data> {
  @ViewChild('tabs', { static: true }) tabs: TabsComponent;

  public currentUser: User;

  public bulkHandlingTabActive = false;
  public teamTabActive = false;
  public fileImportTabActive = false;

  firmData$: Observable<FirmType>;
  firmAssignmentData$: Observable<FirmSettings> = this.firmService.firmAssignmentData$;

  private i18n: FirmSettingsDialogTranslation;

  // eslint-disable-next-line max-params
  constructor(
    private firmService: FirmService,
    private userService: UserService,
    ref: DynamicDialogRef,
    config: DynamicDialogConfig<Data>,
  ) {
    super(ref, config);
    this.currentUser = new User();
    this.firmData$ = this.store.select(FirmSelectors.selectFirm);
  }

  hasRightsToChange() {
    return this.currentUser.role === Role.FIRM_ADMIN;
  }

  resetAndReload() {
    this.userService
      .getCurrentUser()
      .pipe(this.takeUntilDestroyed())
      .subscribe((result) => {
        this.currentUser = result;
      });
  }

  updateFirmData(firmData: FirmType): void {
    this.store.dispatch(FirmActions.saveFirm({ firm: firmData }));
  }

  protected override onTranslationsLoaded(): void {
    this.i18n = {
      firmAssignmentSuccessMessage: this.translate('firmAssignmentSuccessMessage'),
      generalErrorTitle: this.translate('generalErrorTitle'),
      generalErrorMessage: this.translate('generalErrorMessage'),
      resetSuccessMessage: this.translate('resetSuccessMessage'),
    } as FirmSettingsDialogTranslation;
  }

  updateFirmAssignmentData(firmAssignmentData: FirmSettings): void {
    this.firmService.updateFirmSettings(firmAssignmentData).subscribe({
      next: (res) => {
        this.store.dispatch(
          ToastActions.showInfoMessage({ summary: this.i18n.firmAssignmentSuccessMessage, detail: '' }),
        );
        this.firmService.setFirmAssignmentData(res);
      },
      error: (err: unknown) => {
        // eslint-disable-next-line no-console
        console.error('Error while updating assignment data', err);
        this.store.dispatch(
          ToastActions.showErrorMessage({
            summary: this.i18n.generalErrorTitle,
            detail: this.i18n.generalErrorMessage,
          }),
        );
      },
    });
  }

  resetFirmAssignmentData() {
    this.firmService.resetFirmSettings().subscribe({
      next: (x) => {
        this.firmService.setFirmAssignmentData(x);
        this.store.dispatch(ToastActions.showInfoMessage({ summary: this.i18n.resetSuccessMessage, detail: '' }));
      },
      error: (err: unknown) => {
        // eslint-disable-next-line no-console
        console.error('Error while resetting assignment data', err);
        this.store.dispatch(
          ToastActions.showErrorMessage({
            summary: this.i18n.generalErrorTitle,
            detail: this.i18n.generalErrorMessage,
          }),
        );
      },
    });
  }

  resetCallback = () => {
    this.close({});
  };

  clearStorageAndLogout() {
    new UserSettingsStorage().clearSettings(true);
    window.location.href = environment.loginUrl;
  }

  // trackEventWhenActive(active: boolean, tabName: string) {
  //   if (!active) {
  //     return;
  //   }
  //   this.store.dispatch(
  //     AnalyticActions.trackFirmSettingsEvent({
  //       action: AnalyticsAction.FirmSettingsTabs,
  //       name: `Opened ${tabName} tab`,
  //     }),
  //   );
  // }

  protected override onInitialized = () => {
    this.resetAndReload();
    this.firmService.loadFirmAssignmentData();

    const { tabIdToOpen } = this.config.data;

    if (!tabIdToOpen) {
      this.tabs.selectFirst();
    }

    this.tabs.selectTabById(tabIdToOpen);
  };

  protected static override getTranslationScope(): string {
    return TRANSLATION_SCOPE;
  }

  protected static override getDialogConfig(
    translation: Record<string, string>,
  ): Omit<DynamicDialogConfig<Data>, 'data'> {
    return {
      closeOnEscape: true,
      header: translation.firmSettings,
      width: '1500px',
      style: { minHeight: 'min(732px, 90vh)', maxHeight: '90vh' },
      styleClass: 'firm-settings-dialog tabbed-dialog',
      draggable: false,
      modal: true,
    };
  }
}
